import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { setAuthFailureHandler } from '@/services/api/client';

let isNavigating = false;

const routes: RouteRecordRaw[] = [
  {
    path: '/auth/callback',
    name: 'AuthCallback',
    component: () => import('@/pages/AuthCallback.vue'),
    meta: { guest: true },
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/pages/LoginSSO.vue'),
    meta: { guest: true },
  },
  {
    path: '/',
    component: () => import('@/components/layout/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', name: 'Dashboard', component: () => import('@/pages/Dashboard.vue') },
      { path: 'sites', name: 'Sites', component: () => import('@/pages/Sites.vue') },
      { path: 'sites/:id', name: 'SiteDetail', component: () => import('@/pages/SiteDetail.vue'), props: true },
      { path: 'settings', name: 'Settings', component: () => import('@/pages/Settings.vue') },
      { path: 'profile', name: 'Profile', component: () => import('@/pages/Profile.vue') },
      { path: 'admin', name: 'Admin', component: () => import('@/pages/Admin.vue'), meta: { requiresAdmin: true } },
    ],
  },
  {
    path: '/public/:token',
    name: 'PublicSite',
    component: () => import('@/pages/PublicSite.vue'),
    props: true,
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/pages/NotFound.vue'),
  },
];

const router = createRouter({ history: createWebHistory(), routes });

setAuthFailureHandler(() => {
  const authStore = useAuthStore();
  authStore.user = null;
  // On redirige vers /login plutôt que directement vers FusionAuth.
  // FusionAuth maintient sa propre session SSO et répondrait immédiatement avec un nouveau code,
  // créant une boucle infinie si les cookies/tokens ont un problème persistant.
  if (!isNavigating) {
    router.push({ name: 'Login' });
  }
});

router.beforeEach(async (to, _from, next) => {
  isNavigating = true;
  const authStore = useAuthStore();

  const requiresAuth = to.matched.some((r) => r.meta.requiresAuth);
  const isGuest = to.matched.some((r) => r.meta.guest);
  const requiresAdmin = to.matched.some((r) => r.meta.requiresAdmin);

  if (!authStore.user && !isGuest) {
    await authStore.checkAuth();
  }

  if (requiresAuth && !authStore.isAuthenticated) {
    next({ name: 'Login', query: to.fullPath !== '/' ? { redirect: to.fullPath } : {} });
    return;
  }

  if (requiresAdmin && !authStore.isAdmin) {
    next({ name: 'Dashboard' });
    return;
  }

  next();
});

router.afterEach(() => { isNavigating = false; });

export default router;
